import React, { useState } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  IconButton,
  Paper,
  Fade,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface QuizNotificationProps {
  onClose: () => void;
  onDontShowAgain: (dontShow: boolean) => void;
}

export const QuizNotification: React.FC<QuizNotificationProps> = ({
  onClose,
  onDontShowAgain,
}) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleDontShowAgainChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setDontShowAgain(event.target.checked);
  };

  const handleOk = () => {
    onDontShowAgain(dontShowAgain);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Fade in={true} timeout={500}>
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          width: '90%',
          maxWidth: 800,
          background:
            'linear-gradient(135deg,rgb(255, 255, 255) 0%, #e6f3ff 100%)',
          border: '2px solid rgb(73, 132, 234)',
          borderRadius: 3,
          padding: 4,
          boxShadow: '0 8px 32px rgba(255, 179, 217, 0.3)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                background:
                  'linear-gradient(45deg,rgb(102, 153, 255), #87ceeb)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: 3,
                fontSize: '1.4rem',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              🎯 Quick Guide ✨
            </Typography>

            <Box sx={{ marginBottom: 3 }}>
              <Typography
                variant="body1"
                sx={{
                  color: '#5a4a6a',
                  lineHeight: 1.8,
                  marginBottom: 2,
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <span style={{ fontSize: '1.3rem' }}>🎲</span>
                <strong>10 questions per round</strong> — fresh ones plus your
                past slip-ups! 💫
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: '#5a4a6a',
                  lineHeight: 1.8,
                  marginBottom: 2,
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <span style={{ fontSize: '1.3rem' }}>🔄</span>
                <strong>Missed it?</strong> It comes back up again! 🔁
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: '#5a4a6a',
                  lineHeight: 1.8,
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <span style={{ fontSize: '1.3rem' }}>🏆</span>
                <strong>Beat it</strong> to clear it for good! 🎉
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                marginBottom: 2,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={dontShowAgain}
                    onChange={handleDontShowAgainChange}
                    sx={{
                      color: '#ff66b2',
                      '&.Mui-checked': {
                        color: '#87ceeb',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 102, 178, 0.1)',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#5f9ea0',
                      fontSize: '1rem',
                      fontWeight: 500,
                    }}
                  >
                    💭 Don't show this again
                  </Typography>
                }
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                color: '',
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                '&:hover': {
                  color: '#87ceeb',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 3,
            marginTop: 3,
          }}
        >
          <Button
            variant="contained"
            onClick={handleOk}
            sx={{
              background: 'linear-gradient(45deg, #ff4da6, #4682b4)',
              borderRadius: 2,
              padding: '10px 24px',
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: '0 4px 15px rgba(255, 102, 178, 0.4)',
              '&:hover': {
                background: 'linear-gradient(45deg, #ff66b2, #87ceeb)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(255, 102, 178, 0.5)',
              },
              transition: 'all 0.3s ease-in-out',
            }}
          >
            🚀 Got it!
          </Button>
        </Box>
      </Paper>
    </Fade>
  );
};
